import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar, useAuth } from "../components/Shared";
import { mySubscriptions, updateSubscription, fetchVendorDetail, fetchPlans } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { getMockSub } from "../lib/mockSub";
import { PilotDebugPanel } from "../components/PilotDebugPanel";

export default function Dashboard(){
  const navigate = useNavigate();
  const { user, isPilot, configLoading } = useAuth();
  const [subs, setSubs] = useState([]);
  const [vendors, setVendors] = useState({});
  const [plans, setPlans] = useState([]);
  const [planMap, setPlanMap] = useState({});

  // Pilot mode guard: redirect if not bound to vendor (superadmin exempt)
  useEffect(() => {
    if (configLoading) return;
    if (isPilot && user && !user.vendor_id && user.role !== 'superadmin') {
      navigate('/pilot-not-joined', { replace: true });
    }
  }, [isPilot, user, configLoading, navigate]);

  useEffect(()=>{
    const mock = getMockSub();
    if (mock) setSubs([mock]);
    else mySubscriptions().then(setSubs).catch(()=>setSubs([]));
  },[]);
  
  useEffect(()=>{ (async()=>{ try{ const ps = await fetchPlans(); setPlans(ps); setPlanMap(Object.fromEntries(ps.map(p=>[p.id,p]))); }catch{} })(); },[]);

  useEffect(()=>{ (async()=>{ const map={}; for(const s of subs){ if(!map[s.company_id]){ try{ const d=await fetchVendorDetail(s.company_id); map[s.company_id]=d.company; }catch{} } } setVendors(map); })(); },[subs]);

  async function togglePause(s){ if(s.status!=='active' && s.status!=='paused') return; const next = s.status==='active'?'paused':'active'; setSubs(prev=> prev.map(x=> x===s? {...x, status: next }: x)); }
  async function skipWeek(s){ if(s.status!=='active') return; setSubs(prev=> prev.map(x=> x===s? {...x, current_week: { ...(x.current_week||{}), skipped: true }}: x)); }

  // Loading state
  if (configLoading) {
    return (
      <div>
        <Navbar />
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className="animate-pulse mt-4 space-y-3">
            <div className="h-6 bg-neutral-200 rounded w-48 mb-2" />
            <div className="rounded-lg border p-4 flex items-center justify-between gap-4">
              <div className="space-y-2 flex-1">
                <div className="h-4 bg-neutral-200 rounded w-1/2" />
                <div className="h-3 bg-neutral-200 rounded w-3/4" />
              </div>
              <div className="h-9 bg-neutral-200 rounded w-24" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-6">
        <h1 className="text-2xl font-semibold">Your subscriptions</h1>
        <div className="mt-4 space-y-3">
          {subs.length===0 && (<div className="text-sm text-neutral-600">No active subscriptions yet.</div>)}
          {(()=>{
            // Compute most relevant subscription: active if exists, otherwise most recent by our array order
            let list = subs.slice();
            const active = list.find(s=> s.status==='active');
            const primary = active || list[0];
            const rest = active ? list.filter(s=> s!==active) : list.slice(1);
            const items = [];
            if(primary){
              const s = primary;
              const v = vendors[s.company_id];
              const planLabel = planMap[s.plan_id]?.label || s.plan_id;
              items.push(
                <Card key="primary"><CardContent className="p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-medium">{v?.name || s.company_id} • {(s.status||'').toUpperCase()}</div>
                    <div className="text-sm text-neutral-600">Plan: {planLabel || s.plan_id} • This week: {s.current_week?.skipped? 'Skipped' : (s.current_week?.has_selection? 'Selected':'Pending')} {s.current_week?.selection_deadline? `• deadline ${new Date(s.current_week.selection_deadline).toLocaleDateString()}`:''}</div>
                  </div>
                  <div className="flex gap-2">
                    {s.status==='active' && (<Button variant="outline" onClick={()=>skipWeek(s)}>Skip week</Button>)}
                    {s.status==='active' && (<Button className="bg-[#2a7d4f] text-white hover:bg-[#236840]" onClick={()=>togglePause(s)}>Pause</Button>)}
                    {s.status==='paused' && (<Button className="bg-[#2a7d4f] text-white hover:bg-[#236840]" onClick={()=>togglePause(s)}>Resume</Button>)}
                    {/* For MVP, hide Cancel implementation changes; could add when backed */}
                  </div>
                </CardContent></Card>
              );
            }
            if(rest.length>0){
              items.push(
                <details key="history" className="mt-2">
                  <summary className="text-sm cursor-pointer">View history</summary>
                  <div className="mt-2 space-y-2">
                    {rest.map((s, idx)=>{
                      const v = vendors[s.company_id];
                      const planLabel = planMap[s.plan_id]?.label || s.plan_id;
                      return (
                        <Card key={`hist-${idx}`}><CardContent className="p-3 flex items-center justify-between gap-4">
                          <div className="min-w-0">
                            <div className="font-medium">{v?.name || s.company_id} • {(s.status||'').toUpperCase()}</div>
                            <div className="text-sm text-neutral-600">Plan: {planLabel || s.plan_id} • This week: {s.current_week?.skipped? 'Skipped' : (s.current_week?.has_selection? 'Selected':'Pending')}</div>
                          </div>
                        </CardContent></Card>
                      );
                    })}
                  </div>
                </details>
              );
            }
            return items;
          })()}
        </div>
      </div>
      <PilotDebugPanel />
    </div>
  );
}